import { useEffect, useRef, useState } from "react";
import type { ClipboardEvent } from "react";
import {
  Bold,
  Braces,
  Code2,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Palette,
  Redo2,
  RemoveFormatting,
  Underline,
  Undo2
} from "lucide-react";
import { IconButton } from "./ui";

const mergeFields = [
  "CustomerName",
  "GuestName",
  "ReservationNumber",
  "ArrivalDate",
  "DepartureDate",
  "BalanceDue",
  "StaffName"
];

export default function RichTextEditor({
  value,
  onChange,
  minHeight = 220,
  showMergeFields = true
}: {
  value: string;
  onChange: (html: string) => void;
  minHeight?: number;
  showMergeFields?: boolean;
}) {
  const editor = useRef<HTMLDivElement>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const [color, setColor] = useState("#17233c");
  const [showFields, setShowFields] = useState(false);
  const [showHtmlSource, setShowHtmlSource] = useState(false);

  useEffect(() => {
    if (editor.current && editor.current.innerHTML !== value) editor.current.innerHTML = value;
  }, [value]);

  const execute = (command: string, commandValue?: string) => {
    editor.current?.focus();
    document.execCommand(command, false, commandValue);
    if (editor.current) onChange(editor.current.innerHTML);
  };

  const insertField = (field: string) => {
    execute("insertText", `{{${field}}}`);
    setShowFields(false);
  };

  const insertImageFile = (file: File) => {
    if (!/^image\/(png|jpeg|gif|webp)$/i.test(file.type)) {
      window.alert("Choose a PNG, JPEG, GIF, or WebP image.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      window.alert("Inline images must be 8 MB or smaller.");
      return;
    }
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      if (typeof reader.result !== "string") return;
      setShowHtmlSource(false);
      execute("insertImage", reader.result);
    });
    reader.readAsDataURL(file);
  };

  const pasteImage = (event: ClipboardEvent<HTMLDivElement>) => {
    const item = Array.from(event.clipboardData.items).find((entry) => entry.type.startsWith("image/"));
    const file = item?.getAsFile();
    if (!file) return;
    event.preventDefault();
    insertImageFile(file);
  };

  return (
    <div className="rich-editor">
      <div className="editor-toolbar">
        <div className="toolbar-group">
          <IconButton type="button" label="Undo" onClick={() => execute("undo")}>
            <Undo2 size={16} />
          </IconButton>
          <IconButton type="button" label="Redo" onClick={() => execute("redo")}>
            <Redo2 size={16} />
          </IconButton>
        </div>
        <span className="toolbar-divider" />
        <div className="toolbar-group">
          <IconButton type="button" label="Bold" onClick={() => execute("bold")}>
            <Bold size={16} />
          </IconButton>
          <IconButton type="button" label="Italic" onClick={() => execute("italic")}>
            <Italic size={16} />
          </IconButton>
          <IconButton type="button" label="Underline" onClick={() => execute("underline")}>
            <Underline size={16} />
          </IconButton>
        </div>
        <span className="toolbar-divider" />
        <div className="toolbar-group">
          <IconButton type="button" label="Bulleted list" onClick={() => execute("insertUnorderedList")}>
            <List size={16} />
          </IconButton>
          <IconButton type="button" label="Numbered list" onClick={() => execute("insertOrderedList")}>
            <ListOrdered size={16} />
          </IconButton>
          <IconButton
            type="button"
            label="Insert link"
            onClick={() => {
              const url = window.prompt("Link URL");
              if (url) execute("createLink", url);
            }}
          >
            <Link2 size={16} />
          </IconButton>
          <input
            ref={imageInput}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) insertImageFile(file);
              event.target.value = "";
            }}
          />
          <IconButton type="button" label="Insert image" onClick={() => imageInput.current?.click()}>
            <ImagePlus size={16} />
          </IconButton>
          <IconButton
            type="button"
            label={showHtmlSource ? "Show formatted message" : "Edit HTML source"}
            onClick={() => setShowHtmlSource((current) => !current)}
          >
            <Code2 size={16} />
          </IconButton>
        </div>
        <span className="toolbar-divider" />
        <label className="color-control" title="Text color">
          <Palette size={16} />
          <span>Text color</span>
          <input
            type="color"
            value={color}
            onChange={(event) => {
              setColor(event.target.value);
              execute("foreColor", event.target.value);
            }}
          />
        </label>
        <IconButton type="button" label="Clear formatting" onClick={() => execute("removeFormat")}>
          <RemoveFormatting size={16} />
        </IconButton>
        {showMergeFields && (
          <div className="merge-menu-wrap">
            <button
              type="button"
              className="toolbar-text-button"
              onClick={() => setShowFields(!showFields)}
            >
              <Braces size={16} />
              Merge field
            </button>
            {showFields && (
              <div className="merge-menu">
                {mergeFields.map((field) => (
                  <button type="button" key={field} onClick={() => insertField(field)}>
                    {field}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {showHtmlSource ? (
        <textarea
          className="editor-source"
          style={{ minHeight }}
          value={value}
          spellCheck={false}
          onChange={(event) => onChange(event.target.value)}
          aria-label="Email HTML source"
        />
      ) : (
        <div
          ref={editor}
          className="editor-surface"
          style={{ minHeight }}
          contentEditable
          suppressContentEditableWarning
          data-placeholder="Write your message…"
          onInput={(event) => onChange(event.currentTarget.innerHTML)}
          onPaste={pasteImage}
        />
      )}
    </div>
  );
}
